import React, { useState, useEffect } from "react";
import {
  Button,
  DatePicker,
  Table,
  Modal,
  Form,
  Input,
  InputNumber,
  Space,
  message,
  Card,
  Row,
  Col,
  Typography,
  Statistic,
  Tag,
  Select,
  Upload,
} from "antd";
import {
  PlusOutlined,
  DownloadOutlined,
  DeleteOutlined,
  SearchOutlined,
  DollarOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  CalendarOutlined,
  ReloadOutlined,
  UploadOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import "./BanhDatTT.css";
import NavbarTrungThu from "../Layout/NavbarTrungThu";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const BanhDatTT = () => {
  // States
  const [phieuBanHangList, setPhieuBanHangList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [form] = Form.useForm();
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [isDeliveryModalVisible, setIsDeliveryModalVisible] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [deliveryDate, setDeliveryDate] = useState(dayjs());
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [searchText, setSearchText] = useState(""); // Thêm state cho tìm kiếm

  // Initial fetch
  useEffect(() => {
    fetchPhieuBanHang();
    fetchStores();
  }, []);

  // Fetch order data
  const fetchPhieuBanHang = async (showMessage = false) => {
    setLoading(true);
    try {
      const response = await axios.get(
        "https://doticom.vn:3000/api/danhSachPhieuBanHang"
      );

      // Handle direct array response
      if (Array.isArray(response.data)) {
        setPhieuBanHangList(response.data);
        if (showMessage) {
          message.success("Cập nhật dữ liệu bánh trung thu thành công");
        }
      } else if (response.data.success) {
        // Handle wrapped response (if API sometimes returns this format)
        setPhieuBanHangList(response.data.data);
        if (showMessage) {
          message.success("Cập nhật dữ liệu bánh trung thu thành công");
        }
      } else {
        message.error(
          "Lỗi khi tải dữ liệu: " +
            (response.data.error || "Dữ liệu không hợp lệ")
        );
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Lỗi khi tải dữ liệu: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch stores data
  const fetchStores = async () => {
    setStoresLoading(true);
    try {
      const response = await axios.get("https://doticom.vn:3000/stores");

      if (Array.isArray(response.data)) {
        // Filter stores starting with "Bakery" or "BONPAS"
        const filteredStores = response.data.filter(
          (store) =>
            store.TenCuaHang.startsWith("Bakery") ||
            store.TenCuaHang.startsWith("BONPAS")
        );

        // Add CN.HCM option
        const storesWithCNHCM = [
          { IDCH: 163, TenCuaHang: "CN.HCM" },
          ...filteredStores,
        ];

        setStores(storesWithCNHCM);
      } else {
        message.error("Lỗi khi tải danh sách cửa hàng");
      }
    } catch (error) {
      console.error("Error fetching stores:", error);
      message.error("Lỗi khi tải danh sách cửa hàng: " + error.message);
    } finally {
      setStoresLoading(false);
    }
  };

  // Refresh handler
  const handleRefresh = () => {
    fetchPhieuBanHang(true);
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (dates && dates[0] && dates[1]) {
      setDatePickerOpen(false);
    }
  };

  // Get store MaBravo by IDCH
  const getStoreMaBravoByIDCH = (idch) => {
    if (!idch) return "Không xác định";

    // Handle CN.HCM special case
    if (idch === 163 || idch === "163") return "CN.HCM";

    const store = stores.find(
      (store) => store.IDCH === parseInt(idch) || store.IDCH === idch
    );
    return store ? (store.MaBravo || store.TenCuaHang) : `IDCH: ${idch}`;
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!phieuBanHangList || phieuBanHangList.length === 0) {
      message.warning("Không có dữ liệu để xuất");
      return;
    }

    // Sheet 1: Dữ liệu gốc
    const exportData = phieuBanHangList.map((item, index) => ({
      STT: index + 1,
      "ID Phiếu": item.IDPhieu,
      "Tên Khách Hàng": item.TenKH,
      "Người Nhận": getStoreMaBravoByIDCH(item.NguoiNhan),
      "Ngày Đặt": dayjs(item.NgayDat).format("DD/MM/YYYY"),
      "Ngày Giao": item.NgayGiao
        ? dayjs(item.NgayGiao).format("DD/MM/YYYY")
        : "Chưa giao",
      "Trạng Thái": item.Status === 0 ? "Chờ giao" : "Đã giao",
      "Chiết Khấu (%)": item.CK_PT,
      "Chiết Khấu TM": item.CK_TM,
      "SDT": item.SDT || "",
      "Địa Chỉ": item.DiaChi || "",
    }));

    // Sheet 2: File mẫu với thông tin đơn hàng gộp và chi tiết sản phẩm tách riêng
    const templateData = [];
    
    phieuBanHangList.forEach((item, orderIndex) => {
      // Tính tổng giá trị đơn hàng trước chiết khấu
      const totalValue = item.ChiTiet
        ? item.ChiTiet.reduce((sum, detail) => sum + (detail.ThanhTien || 0), 0)
        : 0;
      
      // Tính chiết khấu
      const discountAmount = (totalValue * (item.CK_PT || 0)) / 100;
      const finalValue = totalValue - discountAmount;
      
      // Tính % hoa hồng
      const commission1Percent = totalValue * 0.01; // 1%
      const commission2Percent = totalValue * 0.02; // 2%
      const commission3Percent = totalValue * 0.03; // 3%

      // Tạo chuỗi chi tiết sản phẩm để gộp vào 1 dòng
      const productSummary = item.ChiTiet && item.ChiTiet.length > 0
        ? item.ChiTiet.map(detail => 
            `${detail.MaSP || 'N/A'} (SL: ${detail.SoLuong || 0})`
          ).join('; ')
        : 'Không có sản phẩm';

      // Thông tin tổng hợp của đơn hàng (chỉ 1 dòng cho mỗi đơn)
      const orderSummary = {
        "STT Đơn": orderIndex + 1,
        "ID Phiếu": item.IDPhieu,
        "Khách hàng": item.TenNguoiNhan || item.TenKH,
        "Người nhận": getStoreMaBravoByIDCH(item.NguoiNhan) || "",
        "SĐT": item.SDT || "",
        "Ngày đặt": dayjs(item.NgayDat).format("DD/MM/YYYY"),
        "Ngày giao": item.NgayGiao ? dayjs(item.NgayGiao).format("DD/MM/YYYY") : "Chưa giao",
        "Trạng thái": item.Status === 0 ? "Chờ giao" : "Đã giao",
        "Nhân sự giới thiệu (1%)": item.CK_GioiThieu || "",
        "Nhân sự chốt đơn (2%)": item.CK_ChotDon || "",
        "Bộ phận đảm nhận đơn hàng (3%)": item.CK_GiaoHang || "",
        "Chi tiết sản phẩm": productSummary,
        "Tổng giá trị đơn hàng (VNĐ)": totalValue,
        "Mức chiết khấu": `${item.CK_PT || 0}%`,
        "Tổng tiền chiết khấu": discountAmount,
        "Giá trị đơn hàng sau chiết khấu (VNĐ)": finalValue,
        "Hoa hồng giới thiệu (1%)": commission1Percent,
        "Hoa hồng chốt đơn (2%)": commission2Percent,
        "Hoa hồng đảm nhận (3%)": commission3Percent,
      };

      templateData.push(orderSummary);
    });

    // Sheet 3: Chi tiết sản phẩm theo từng dòng
    const detailData = [];
    
    phieuBanHangList.forEach((item, orderIndex) => {
      if (item.ChiTiet && item.ChiTiet.length > 0) {
        item.ChiTiet.forEach((detail, detailIndex) => {
          detailData.push({
            "STT Đơn": orderIndex + 1,
            "ID Phiếu": item.IDPhieu,
            "Khách hàng": item.TenNguoiNhan || item.TenKH,
            "STT SP": detailIndex + 1,
            "Mã sản phẩm": detail.MaSP || "",
            "Tên sản phẩm": detail.TenSP || "",
            "Số lượng": detail.SoLuong || 0,
            "Đơn giá": detail.DonGia || 0,
            "Thành tiền": detail.ThanhTien || 0,
          });
        });
      } else {
        // Nếu không có chi tiết sản phẩm
        detailData.push({
          "STT Đơn": orderIndex + 1,
          "ID Phiếu": item.IDPhieu,
          "Khách hàng": item.TenNguoiNhan || item.TenKH,
          "STT SP": "",
          "Mã sản phẩm": "Không có sản phẩm",
          "Tên sản phẩm": "",
          "Số lượng": 0,
          "Đơn giá": 0,
          "Thành tiền": 0,
        });
      }
    });

    // Tạo workbook với 3 sheets
    const wb = XLSX.utils.book_new();
    
    // Sheet 1: Dữ liệu gốc
    const ws1 = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.book_append_sheet(wb, ws1, "Dữ Liệu Bánh Trung Thu");
    
    // Sheet 2: Tổng hợp chiết khấu (1 dòng/đơn hàng)
    const ws2 = XLSX.utils.json_to_sheet(templateData);
    const colWidths2 = [
      { wch: 8 },   // STT Đơn
      { wch: 10 },  // ID Phiếu
      { wch: 25 },  // Khách hàng
      { wch: 20 },  // Người nhận
      { wch: 15 },  // SĐT
      { wch: 12 },  // Ngày đặt
      { wch: 12 },  // Ngày giao
      { wch: 12 },  // Trạng thái
      { wch: 20 },  // Nhân sự giới thiệu
      { wch: 20 },  // Nhân sự chốt đơn
      { wch: 25 },  // Bộ phận đảm nhận
      { wch: 40 },  // Chi tiết sản phẩm
      { wch: 18 },  // Tổng giá trị đơn hàng
      { wch: 12 },  // Mức chiết khấu
      { wch: 18 },  // Tổng tiền chiết khấu
      { wch: 22 },  // Giá trị sau chiết khấu
      { wch: 18 },  // Hoa hồng giới thiệu
      { wch: 18 },  // Hoa hồng chốt đơn
      { wch: 18 },  // Hoa hồng đảm nhận
    ];
    ws2['!cols'] = colWidths2;
    XLSX.utils.book_append_sheet(wb, ws2, "Chiet_Khau");
    
    // Sheet 3: Chi tiết sản phẩm (nhiều dòng/đơn hàng)
    const ws3 = XLSX.utils.json_to_sheet(detailData);
    const colWidths3 = [
      { wch: 8 },   // STT Đơn
      { wch: 10 },  // ID Phiếu
      { wch: 25 },  // Khách hàng
      { wch: 8 },   // STT SP
      { wch: 15 },  // Mã sản phẩm
      { wch: 30 },  // Tên sản phẩm
      { wch: 10 },  // Số lượng
      { wch: 15 },  // Đơn giá
      { wch: 15 },  // Thành tiền
    ];
    ws3['!cols'] = colWidths3;
    XLSX.utils.book_append_sheet(wb, ws3, "Chi_Tiet_San_Pham");

    const fileName = `BanhTrungThu_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`;
    XLSX.writeFile(wb, fileName);
    message.success("Xuất Excel thành công với 3 sheets: Dữ liệu gốc, Chiết khấu tổng hợp, và Chi tiết sản phẩm");
  };

  // Show add order modal
  const showAddModal = () => {
    setIsEditMode(false);
    setEditingOrder(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  // Show edit order modal
  const showEditModal = (order) => {
    setIsEditMode(true);
    setEditingOrder(order);
    form.setFieldsValue({
      TenKH: order.TenKH,
      NguoiNhan: order.NguoiNhan,
      NgayDat: dayjs(order.NgayDat),
      TenNguoiNhan: order.TenNguoiNhan,
      MaKhoi: order.MaKhoi,
      MaNhom: order.MaNhom,
      CK_GioiThieu: order.CK_GioiThieu,
      CK_GiaoHang: order.CK_GiaoHang,
      CK_ChotDon: order.CK_ChotDon,
      CK_PT: order.CK_PT,
      CK_TM: order.CK_TM,
      ChiTiet: order.ChiTiet || [{}],
      SDT: order.SDT || "",  // Added for SDT
      DiaChi: order.DiaChi || "",  // Added for DiaChi
      GhiChu: order.GhiChu || "",
    });
    setIsModalVisible(true);
  };

  // Show delivery modal
  const showDeliveryModal = (id) => {
    setSelectedOrderId(id);
    setDeliveryDate(dayjs());
    setIsDeliveryModalVisible(true);
  };

  // Handle delivery modal submit
  const handleDeliverySubmit = async () => {
    try {
      setLoading(true);
      const response = await axios.put(
        `https://doticom.vn:3000/api/capNhatPhieuBanHang/${selectedOrderId}`,
        { Status: 1, NgayGiao: deliveryDate.format("YYYY-MM-DD") }
      );

      if (response.status === 200 && response.data.message) {
        message.success("Cập nhật ngày giao thành công");
        setIsDeliveryModalVisible(false);
        fetchPhieuBanHang(false);
      } else {
        message.error(response.data.message || "Lỗi khi cập nhật ngày giao");
      }
    } catch (error) {
      console.error("Delivery update error:", error);
      message.error("Lỗi khi cập nhật ngày giao: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle toggle status
  const handleToggleStatus = async (id, newStatus) => {
    try {
      setLoading(true);
      const body = newStatus === 0 ? { Status: 0, NgayGiao: null } : { Status: 1 };
      const response = await axios.put(
        `https://doticom.vn:3000/api/capNhatPhieuBanHang/${id}`,
        body
      );

      if (response.status === 200 && response.data.message) {
        message.success(newStatus === 0 ? "Đã chuyển về chờ giao" : "Đã giao thành công");
        fetchPhieuBanHang(false);
      } else {
        message.error(response.data.message || "Lỗi khi cập nhật trạng thái");
      }
    } catch (error) {
      console.error("Status update error:", error);
      message.error("Lỗi khi cập nhật trạng thái: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Submit form
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const formattedValues = {
        TenKH: values.TenKH ? values.TenKH.trim() : "",
        LoaiPhieu: values.NguoiNhan ? 1 : 0 || 0,
        SDT: values.SDT ? values.SDT.trim() : "",  // Already present, now matched to form
        DiaChi: values.DiaChi ? values.DiaChi.trim() : "",  // Added for DiaChi
         GhiChu: values.GhiChu ? values.GhiChu.trim() : "",
        NguoiNhan: values.NguoiNhan ? values.NguoiNhan.toString() : "",
        NgayDat: values.NgayDat.format("YYYY-MM-DD"),
        CK_PT: values.CK_PT || 0,
        CK_TM: values.CK_TM || 0,
        MaCH: values.MaCH || 0,
        MaCH_CT: values.MaCH_CT || 0,
        TenNguoiNhan: values.TenNguoiNhan || "",
        MaKhoi: values.MaKhoi || '0',
        MaNhom: values.MaNhom || '0',
        CK_GioiThieu: values.CK_GioiThieu || "",
        CK_GiaoHang: values.CK_GiaoHang || "",
        CK_ChotDon: values.CK_ChotDon || "",
        ChiTiet: (values.ChiTiet || [])
          .filter((item) => item && item.MaSP && item.SoLuong)
          .map((item) => ({
            MaSP: item.MaSP ? item.MaSP.trim() : "",
            SoLuong: parseInt(item.SoLuong) || 0,
          })),
      };

      if (!formattedValues.ChiTiet || formattedValues.ChiTiet.length === 0) {
        message.error("Vui lòng thêm ít nhất một sản phẩm hợp lệ");
        return;
      }

      setLoading(true);

      let response;
      if (isEditMode) {
        response = await axios.put(
          `https://doticom.vn:3000/api/capNhatPhieuBanHang/${editingOrder.IDPhieu}`,
          formattedValues
        );
      } else {
        response = await axios.post(
          "https://doticom.vn:3000/api/themPhieuBanHang",
          formattedValues
        );
      }

      if ((response.status === 200 || response.status === 201) && response.data.message) {
        message.success(isEditMode ? "Cập nhật đơn bánh trung thu thành công" : "Thêm đơn bánh trung thu thành công");
        setIsModalVisible(false);
        fetchPhieuBanHang(false);
      } else {
        message.error("Lỗi khi lưu đơn hàng");
      }
    } catch (error) {
      console.error("Submit error:", error);
      if (error.response && error.response.status === 400 && error.response.data && error.response.data.message) {
        message.error(error.response.data.message);
      } else {
        message.error("Lỗi khi lưu đơn hàng: " + (error.message || "Lỗi không xác định"));
      }
    } finally {
      setLoading(false);
    }
  };

  // Delete order
  const handleDelete = async (id) => {
    Modal.confirm({
      title: "Xác nhận xóa",
      content: "Bạn có chắc chắn muốn xóa đơn hàng này?",
      okText: "Xóa",
      okType: "danger",
      cancelText: "Hủy",
      centered: true,
      width: 400,
      onOk: async () => {
        try {
          setLoading(true);
          const response = await axios.delete(
            `https://doticom.vn:3000/api/xoaPhieuBanHang/${id}`
          );

          // Handle API response with message field
          if (response.data.message) {
            message.success("Xóa đơn hàng thành công");
            fetchPhieuBanHang(false);
          } else if (response.data.success) {
            // Handle wrapped response (if API sometimes returns this format)
            message.success("Xóa đơn hàng thành công");
            fetchPhieuBanHang(false);
          } else {
            message.error(
              "Lỗi khi xóa: " + (response.data.error || "Phản hồi không hợp lệ")
            );
          }
        } catch (error) {
          console.error("Delete error:", error);
          message.error("Lỗi khi xóa: " + error.message);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Calculate summary statistics
  const calculateSummary = () => {
    if (!phieuBanHangList || phieuBanHangList.length === 0) {
      return {
        totalOrders: 0,
        pendingOrders: 0,
        deliveredOrders: 0,
        totalRevenue: 0,
      };
    }

    return phieuBanHangList.reduce(
      (summary, item) => {
        const orderTotal = item.ChiTiet
          ? item.ChiTiet.reduce(
              (sum, detail) => sum + (detail.ThanhTien || 0),
              0
            )
          : 0;
        const discountAmount =
          (orderTotal * (item.CK_PT || 0)) / 100 + (item.CK_TM || 0);
        const finalAmount = orderTotal - discountAmount;

        return {
          totalOrders: summary.totalOrders + 1,
          pendingOrders: summary.pendingOrders + (item.Status === 0 ? 1 : 0),
          deliveredOrders:
            summary.deliveredOrders + (item.Status === 1 ? 1 : 0),
          totalRevenue: summary.totalRevenue + finalAmount,
        };
      },
      {
        totalOrders: 0,
        pendingOrders: 0,
        deliveredOrders: 0,
        totalRevenue: 0,
      }
    );
  };

  const summaryData = calculateSummary();

  // Calculate order total amount
  const calculateOrderTotal = (order) => {
    if (!order.ChiTiet || order.ChiTiet.length === 0) return 0;
    
    // Tính tổng tiền của tất cả sản phẩm
    const subtotal = order.ChiTiet.reduce(
      (sum, detail) => sum + (detail.ThanhTien || 0),
      0
    );
    
    // Tính tổng tiền của sản phẩm H4BTTCC
    const h4bttccTotal = order.ChiTiet
      .filter(detail => detail.MaSP && detail.MaSP.trim() === "H4BTTCC")
      .reduce((sum, detail) => sum + (detail.ThanhTien || 0), 0);
    
    // Tổng tiền của các sản phẩm khác (không phải H4BTTCC)
    const otherTotal = subtotal - h4bttccTotal;
    
    // Áp dụng chiết khấu chỉ cho phần otherTotal
    const discountAmount = (otherTotal * (order.CK_PT || 0)) / 100 + (order.CK_TM || 0);
    
    // Tổng cuối cùng: (otherTotal sau chiết khấu) + h4bttccTotal
    return otherTotal - discountAmount + h4bttccTotal;
  };

  // Expandable row content
  const expandedRowRender = (record) => {
    if (!record.ChiTiet || record.ChiTiet.length === 0) {
      return <Text type="secondary">Không có chi tiết sản phẩm</Text>;
    }

    const detailColumns = [
      {
        title: "Mã SP",
        dataIndex: "MaSP",
        key: "MaSP",
        render: (text) => (text ? text.trim() : ""),
      },
      {
        title: "Tên Sản Phẩm",
        dataIndex: "TenSP",
        key: "TenSP",
      },
      {
        title: "Số Lượng",
        dataIndex: "SoLuong",
        key: "SoLuong",
        render: (text) => (text ? text.toString().trim() : "0"),
      },
      {
        title: "Đơn Giá",
        dataIndex: "DonGia",
        key: "DonGia",
        render: (value) =>
          value ? value.toLocaleString("vi-VN") + " đ" : "0 đ",
      },
      {
        title: "Thành Tiền",
        dataIndex: "ThanhTien",
        key: "ThanhTien",
        render: (value) =>
          value ? value.toLocaleString("vi-VN") + " đ" : "0 đ",
      },
    ];

    const subtotal = record.ChiTiet.reduce(
      (sum, detail) => sum + (detail.ThanhTien || 0),
      0
    );
    
    // Tính tổng tiền của sản phẩm H4BTTCC
    const h4bttccTotal = record.ChiTiet
      .filter(detail => detail.MaSP && detail.MaSP.trim() === "H4BTTCC")
      .reduce((sum, detail) => sum + (detail.ThanhTien || 0), 0);
    
    // Tổng tiền của các sản phẩm khác
    const otherTotal = subtotal - h4bttccTotal;
    
    // Áp dụng chiết khấu chỉ cho otherTotal
    const discountAmount = (otherTotal * (record.CK_PT || 0)) / 100 + (record.CK_TM || 0);
    
    // Tổng cuối cùng
    const finalTotal = otherTotal - discountAmount + h4bttccTotal;

    return (
      <div style={{ padding: "16px", backgroundColor: "#fafafa" }}>
        <Table
          columns={detailColumns}
          dataSource={record.ChiTiet}
          pagination={false}
          rowKey="IDPhieu_CT"
          size="small"
        />
        <div style={{ marginTop: "16px", textAlign: "right" }}>
          <Space direction="vertical" size="small">
            <Text>
              Tạm tính: <strong>{subtotal.toLocaleString("vi-VN")} đ</strong>
            </Text>
            {(record.CK_PT > 0 || record.CK_TM > 0) && (
              <Text>
                Chiết khấu (áp dụng cho sản phẩm khác H4BTTCC):{" "}
                <strong style={{ color: "#ff4d4f" }}>
                  -{discountAmount.toLocaleString("vi-VN")} đ
                </strong>
              </Text>
            )}
            <Text style={{ fontSize: "16px" }}>
              Tổng cộng:{" "}
              <strong style={{ color: "#1890ff" }}>
                {finalTotal.toLocaleString("vi-VN")} đ
              </strong>
            </Text>
          </Space>
        </div>
      </div>
    );
  };

  // Get store name by IDCH
  const getStoreNameByIDCH = (idch) => {
    if (!idch) return "Không xác định";

    // Handle CN.HCM special case
    if (idch === 163 || idch === "163") return "CN.HCM";

    const store = stores.find(
      (store) => store.IDCH === parseInt(idch) || store.IDCH === idch
    );
    return store ? store.TenCuaHang : `IDCH: ${idch}`;
  };

  // Columns for order list table
  const columns = [
    {
      title: "STT",
      key: "STT",
      render: (_, __, index) => index + 1,
      width: 60,
      align: "center",
      fixed: "left",
    },
    {
      title: "Tên Khách Hàng",
      dataIndex: "TenKH",
      key: "TenKH",
      ellipsis: true,
    },
    {
      title: "Người Nhận",
      dataIndex: "NguoiNhan",
      key: "NguoiNhan",
      render: (idch) => getStoreNameByIDCH(idch),
      ellipsis: true,
    },
    {
      title: "Ngày Đặt",
      dataIndex: "NgayDat",
      key: "NgayDat",
      render: (date) => dayjs(date).format("DD/MM/YYYY"),
      sorter: (a, b) => dayjs(a.NgayDat).unix() - dayjs(b.NgayDat).unix(),
      align: "center",
    },
    {
      title: "Ngày Giao",
      dataIndex: "NgayGiao",
      key: "NgayGiao",
      render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "Chưa giao"),
      sorter: (a, b) => {
        if (!a.NgayGiao && !b.NgayGiao) return 0;
        if (!a.NgayGiao) return 1;
        if (!b.NgayGiao) return -1;
        return dayjs(a.NgayGiao).unix() - dayjs(b.NgayGiao).unix();
      },
      align: "center",
    },
    {
      title: "Trạng Thái",
      dataIndex: "Status",
      key: "Status",
      render: (status) => (
        <Tag
          color={status === 0 ? "orange" : "green"}
          className="cake-type-badge"
        >
          {status === 0 ? "Chờ giao" : "Đã giao"}
        </Tag>
      ),
      sorter: (a, b) => a.Status - b.Status,
      align: "center",
    },
    {
      title: "Tổng Tiền",
      key: "TongTien",
      render: (_, record) => {
        const total = calculateOrderTotal(record);
        return (
          <Text strong style={{ color: total > 0 ? "#1890ff" : "#666" }}>
            {total.toLocaleString("vi-VN")} đ
          </Text>
        );
      },
      align: "right",
    },
    {
      title: "CK (%)",
      dataIndex: "CK_PT",
      key: "CK_PT",
      align: "center",
    },
    {
      title: "CK TM",
      dataIndex: "CK_TM",
      key: "CK_TM",
      render: (value) => (value ? value.toLocaleString("vi-VN") + " đ" : "0 đ"),
      align: "right",
    },
    {
      title: "Ghi chú",
      dataIndex: "GhiChu",
      key: "GhiChu",
      render: (text) => text || <span style={{ color: "#aaa" }}>-</span>,
      ellipsis: true,
    },
    {
      title: "Thao Tác",
      key: "actions",
      render: (_, record) => (
        <Row gutter={8} justify="center">
          <Col span={8}>
            {record.Status === 0 ? (
              <Button
                type="primary"
                size="small"
                onClick={() => showDeliveryModal(record.IDPhieu)}
                style={{ fontSize: '12px', padding: '4px 8px', height: 'auto' }}
              >
                Đã Giao
              </Button>
            ) : (
              <Button
                type="default"
                size="small"
                onClick={() => handleToggleStatus(record.IDPhieu, 0)}
                style={{ fontSize: '12px', padding: '4px 8px', height: 'auto' }}
              >
                Chờ giao
              </Button>
            )}
          </Col>
          <Col span={8}>
            <Button
              type="default"
              size="small"
              onClick={() => showEditModal(record)}
              style={{ fontSize: '12px', padding: '4px 8px', height: 'auto' }}
            >
              Chỉnh Sửa
            </Button>
          </Col>
          <Col span={8}>
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.IDPhieu)}
              style={{ fontSize: '12px', padding: '4px 8px', height: 'auto' }}
            >
              Xóa
            </Button>
          </Col>
        </Row>
      ),
      align: "center",
      fixed: "right",
    },
  ];

  // Import Excel handler
  const handleImportExcel = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // Skip header row and process data
        const importedItems = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (row[0] && row[1]) {
            // MaSP and SL exist
            importedItems.push({
              MaSP: row[0].toString().trim(),
              SoLuong: parseInt(row[1]) || 1,
            });
          }
        }

        if (importedItems.length > 0) {
          // Get current ChiTiet values
          const currentChiTiet = form.getFieldValue("ChiTiet") || [];
          // Filter out empty items and merge with imported
          const filteredCurrent = currentChiTiet.filter(
            (item) => item && item.MaSP && item.SoLuong
          );
          const mergedItems = [...filteredCurrent, ...importedItems];

          // Update form
          form.setFieldsValue({ ChiTiet: mergedItems });
          message.success(
            `Đã import thành công ${importedItems.length} sản phẩm từ Excel`
          );
        } else {
          message.warning("Không tìm thấy dữ liệu hợp lệ trong file Excel");
        }
      } catch (error) {
        console.error("Import error:", error);
        message.error("Lỗi khi đọc file Excel: " + error.message);
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // Prevent upload
  };

  // Download Excel template
  const downloadExcelTemplate = () => {
    const templateData = [
      ["MaSP", "SL"],
      ["Bu002", "2"],
      ["", ""],
    ];

    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");

    const fileName = `Template_ChiTietBanh_${dayjs().format("YYYYMMDD")}.xlsx`;
    XLSX.writeFile(wb, fileName);
    message.success("Đã tải file mẫu thành công");
  };

  // Handle modal cancel
  const handleCancel = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setEditingOrder(null);
    form.resetFields();
  };

  // Lọc danh sách theo tên khách hàng
  const filteredPhieuBanHangList = phieuBanHangList.filter((item) =>
    item.TenKH && item.TenKH.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <> 
    <NavbarTrungThu />
    <div className="banh-dat-tt-container">
      <Card className="autumn-animation">
        <div className="header-container">
          <Title level={2}>
            <span>
              <span style={{ filter: "none", color: "initial" }}>🥮</span> Quản
              Lý Đặt Bánh Trung Thu
            </span>
          </Title>

          <Space wrap>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              title="Cập nhật dữ liệu"
            >
              Cập nhật
            </Button>
            <Button icon={<DownloadOutlined />} onClick={exportToExcel}>
              Xuất Excel
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showAddModal}
              className="autumn-animation"
            >
              Thêm Đơn Bánh
            </Button>
          </Space>
        </div>

        {/* Thanh tìm kiếm theo tên khách hàng */}
        <div style={{ margin: "16px 0" }}>
          <Input
            placeholder="Tìm kiếm theo tên khách hàng..."
            prefix={<SearchOutlined />}
            allowClear
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 300 }}
          />
        </div>

        {/* Summary Statistics */}
        <div className="summary-section">
          <Row gutter={16}>
            <Col xs={24} sm={6} md={6}>
              <Card className="summary-card">
                <Statistic
                  title="Tổng Đơn Hàng"
                  value={summaryData.totalOrders}
                  prefix={<ShoppingCartOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6} md={6}>
              <Card className="summary-card">
                <Statistic
                  title="Chờ Giao Hàng"
                  value={summaryData.pendingOrders}
                  prefix={<CalendarOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6} md={6}>
              <Card className="summary-card">
                <Statistic
                  title="Đã Giao Hàng"
                  value={summaryData.deliveredOrders}
                  prefix={<ShoppingCartOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6} md={6}>
              <Card className="summary-card">
                <Statistic
                  title="Tổng Doanh Thu"
                  value={summaryData.totalRevenue}
                  prefix={<DollarOutlined />}
                  formatter={(value) => `${value.toLocaleString("vi-VN")} đ`}
                />
              </Card>
            </Col>
          </Row>
        </div>

        <Table
          columns={columns}
          dataSource={filteredPhieuBanHangList}
          rowKey="IDPhieu"
          loading={loading}
          expandable={{
            expandedRowRender,
            rowExpandable: (record) =>
              record.ChiTiet && record.ChiTiet.length > 0,
          }}
          pagination={{
            pageSize: 10,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} đơn hàng`,
          }}
          className="autumn-animation"
          scroll={{ x: true }} // Thêm scroll ngang nếu bảng rộng
        />
      </Card>

      {/* Modal thêm/chỉnh sửa đơn bánh trung thu */}
      <Modal
        title={
          <span>
            <span style={{ filter: "none", color: "initial" }}>🥮</span>{" "}
            {isEditMode ? "Chỉnh Sửa" : "Thêm"} Đơn Bánh Trung Thu
          </span>
        }
        open={isModalVisible}
        onCancel={handleCancel}
        width={800}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Hủy
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleSubmit}
          >
            {isEditMode ? "Cập Nhật" : "Lưu"}
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            NgayDat: dayjs(),
            CK_PT: 0,
            CK_TM: 0,
            ChiTiet: [{}],
            MaCH: 0,
            MaCH_CT: 0,
            TenNguoiNhan: "",
            MaKhoi: '0',
            MaNhom: '0',
            CK_GioiThieu: "",
            CK_GiaoHang: "",
            CK_ChotDon: "",
            SDT: "",  // Added initial value for SDT
            DiaChi: "",  // Added initial value for DiaChi
            GhiChu: "",
          }}
        >
          {/* Thông tin khách hàng */}
          <Card title="Thông Tin Khách Hàng" className="form-card">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="TenKH"
                  label="Tên Khách Hàng"
                  rules={[
                    { required: true, message: "Vui lòng nhập tên khách hàng" },
                  ]}
                >
                  <Input
                    placeholder="Nhập tên khách hàng"
                    prefix={<UserOutlined />}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="NguoiNhan" label="Cửa hàng">
                  <Select
                    placeholder="Chọn cửa hàng"
                    loading={storesLoading}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.children ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {stores.map((store) => (
                      <Select.Option key={store.IDCH} value={store.IDCH}>
                        {store.TenCuaHang}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="SDT"
                  label="Số Điện Thoại"
                  rules={[
                    { pattern: /^[0-9+\-\s()]+$/, message: "Số điện thoại không hợp lệ" },  // Optional validation for phone
                  ]}
                >
                  <Input placeholder="Nhập số điện thoại" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="DiaChi" label="Địa Chỉ">
                  <Input placeholder="Nhập địa chỉ" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="NgayDat"
                  label="Ngày Đặt"
                  rules={[
                    { required: true, message: "Vui lòng chọn ngày đặt" },
                  ]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY"
                    placeholder="Chọn ngày đặt"
                    suffixIcon={<CalendarOutlined />}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
          <Card title="Thông Tin Bổ Sung" className="form-card">
            <Row gutter={16}></Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="TenNguoiNhan"
                  label="Tên Người Nhận"
                  initialValue=""
                >
                  <Input placeholder="Nhập tên người nhận" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="MaKhoi" label="Mã Khối" initialValue="VP">
                  <Select placeholder="Chọn mã khối">
                    <Select.Option value="VP">VP</Select.Option>
                    <Select.Option value="DTSG">DTSG</Select.Option>
                    <Select.Option value="Bonpas">Bonpas</Select.Option>
                    <Select.Option value="XSX">XSX</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="MaNhom" label="Mã Nhóm" initialValue="Main">
                  <Select placeholder="Chọn mã nhóm">
                    <Select.Option value="Main">Main</Select.Option>
                    <Select.Option value="Nhom1">Nhom1</Select.Option>
                    <Select.Option value="Nhom2">Nhom2</Select.Option>
                    <Select.Option value="Nhom3">Nhom3</Select.Option>
                    <Select.Option value="Nhom4">Nhom4</Select.Option>
                    <Select.Option value="Nhom5">Nhom5</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="CK_GioiThieu"
                  label="CK Giới Thiệu"
                  initialValue=""
                >
                  <Input placeholder="Nhập CK giới thiệu" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="CK_GiaoHang"
                  label="CK Giao Hàng"
                  initialValue=""
                >
                  <Input placeholder="Nhập CK giao hàng" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="CK_ChotDon"
                  label="CK Chốt Đơn"
                  initialValue=""
                >
                  <Input placeholder="Nhập CK chốt đơn" />
                </Form.Item>
              </Col>
            </Row>
          </Card>
          {/* Chi tiết đơn hàng */}
          <Card title="Chi Tiết Bánh" className="form-card">
            <div style={{ marginBottom: "16px" }}>
              <Space wrap>
                <Upload
                  accept=".xlsx,.xls"
                  beforeUpload={handleImportExcel}
                  showUploadList={false}
                >
                  <Button icon={<UploadOutlined />} type="default">
                    Import Excel
                  </Button>
                </Upload>
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={downloadExcelTemplate}
                  type="dashed"
                >
                  Tải File Mẫu
                </Button>
              </Space>
              <div
                style={{
                  marginTop: "8px",
                  color: "#666",
                  fontSize: "12px",
                }}
              >
                Định dạng file Excel: Cột A (MaSP), Cột B (SL)
              </div>
            </div>
            <Form.List name="ChiTiet">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <div key={field.key} className="cake-order-item">
                      <Row gutter={16} align="middle">
                        <Col span={10}>
                          <Form.Item
                            {...field}
                            name={[field.name, "MaSP"]}
                            rules={[
                              { required: true, message: "Nhập mã bánh" },
                            ]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input placeholder="Mã sản phẩm bánh" />
                          </Form.Item>
                        </Col>
                        <Col span={10}>
                          <Form.Item
                            {...field}
                            name={[field.name, "SoLuong"]}
                            rules={[
                              { required: true, message: "Nhập số lượng" },
                            ]}
                            style={{ marginBottom: 0 }}
                          >
                            <InputNumber
                              style={{ width: "100%" }}
                              placeholder="Số lượng"
                              min={1}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(field.name)}
                          />
                        </Col>
                      </Row>
                    </div>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                      className="add-item-button"
                    >
                      Thêm Bánh
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Card>
          {/* Chiết khấu */}
          <Card title="Chiết Khấu" className="form-card">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="CK_PT" label="Chiết Khấu (%)" initialValue={0}>
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    max={100}
                    placeholder="Nhập % chiết khấu"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="CK_TM"
                  label="Chiết Khấu Tiền Mặt"
                  initialValue={0}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                    placeholder="Nhập chiết khấu tiền mặt"
                    prefix={<DollarOutlined />}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
          {/* Thêm trường ghi chú */}
          <Card title="Ghi Chú" className="form-card">
            <Form.Item name="GhiChu" label="Ghi chú">
              <Input.TextArea rows={2} placeholder="Nhập ghi chú cho phiếu (nếu có)" />
            </Form.Item>
          </Card>
        </Form>
      </Modal>

      {/* Modal chọn ngày giao */}
      <Modal
        title="Chọn Ngày Giao"
        open={isDeliveryModalVisible}
        onCancel={() => setIsDeliveryModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setIsDeliveryModalVisible(false)}>
            Hủy
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleDeliverySubmit}
          >
            Xác Nhận
          </Button>,
        ]}
      >
        <Form layout="vertical">
          <Form.Item label="Ngày Giao">
            <DatePicker
              value={deliveryDate}
              onChange={setDeliveryDate}
              format="DD/MM/YYYY"
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
    </>
  );
};

export default BanhDatTT;
